/**
 * Extract stable identifier from part code
 * Format: "CS0036-299-B-12-25-4414-002" -> "25-4414-002" (last 3 segments)
 * @param {string} partCode - The full part code
 * @returns {string} The stable identifier or 'N/A' if invalid
 */
export const extractStableIdentifier = (partCode) => {
  if (!partCode || partCode === 'N/A') {
    return 'N/A';
  }

  const parts = partCode.split('-');
  if (parts.length >= 3) {
    return parts.slice(-3).join('-');
  }

  return partCode;
};

/**
 * Get human-readable label for RFQ reason code
 * @param {string} code - The reason code
 * @param {number} [limit] - Optional limit value to include in the label
 * @returns {string} Human-readable label
 */
export const getReasonTypeLabel = (code, limit = null) => {
  switch (code) {
    case 'BENDING_SELECTED':
      return 'Bending Service Selected';
    case 'QUANTITY_EXCEEDS':
      return limit ? `Quantity exceeds automatic pricing calculation (>${limit} units)` : 'Quantity exceeds automatic pricing calculation';
    case 'NON_STOCK_MATERIAL':
      return 'Non-Stock Material';
    case 'WEIGHT_EXCEEDS':
      return limit ? `Weight exceeds automatic shipping rate calculation (>${limit} lbs)` : 'Weight exceeds automatic shipping rate calculation';
    case 'WIDTH_EXCEEDS':
      return limit ? `Width exceeds automatic shipping rate calculation (>${limit}")` : 'Width exceeds automatic shipping rate calculation';
    case 'HEIGHT_EXCEEDS':
      return limit ? `Height exceeds automatic shipping rate calculation (>${limit}")` : 'Height exceeds automatic shipping rate calculation';
    default:
      return 'Requires Manual Review';
  }
};

/**
 * Get human-readable label parts for RFQ reason code (main text and threshold separately)
 * @param {string} code - The reason code
 * @param {number} [limit] - Optional limit value to include
 * @returns {Object} Object with 'main' and 'threshold' properties
 */
export const getReasonTypeLabelParts = (code, limit = null) => {
  const getThreshold = () => {
    if (!limit) return null;

    switch (code) {
      case 'QUANTITY_EXCEEDS':
        return `>${limit} units`;
      case 'WEIGHT_EXCEEDS':
        return `>${limit} lbs`;
      case 'WIDTH_EXCEEDS':
      case 'HEIGHT_EXCEEDS':
        return `>${limit}"`;
      default:
        return null;
    }
  };

  const getMainLabel = () => {
    switch (code) {
      case 'BENDING_SELECTED':
        return 'Bending Service Selected';
      case 'QUANTITY_EXCEEDS':
        return 'Quantity exceeds automatic pricing calculation';
      case 'NON_STOCK_MATERIAL':
        return 'Non-Stock Material';
      case 'WEIGHT_EXCEEDS':
        return 'Weight exceeds automatic shipping rate calculation';
      case 'WIDTH_EXCEEDS':
        return 'Width exceeds automatic shipping rate calculation';
      case 'HEIGHT_EXCEEDS':
        return 'Height exceeds automatic shipping rate calculation';
      default:
        return 'Requires Manual Review';
    }
  };

  return {
    main: getMainLabel(),
    threshold: getThreshold()
  };
};

/**
 * Group RFQ items by reason code
 * @param {Array} items - Array of items with rfq_reasons
 * @returns {Object} Items grouped by reason code
 */
export const groupByReasonCode = (items) => {
  const grouped = {};

  items.forEach(item => {
    item.rfq_reasons.forEach(reason => {
      const code = reason.code;
      if (!grouped[code]) {
        grouped[code] = [];
      }
      grouped[code].push({
        ...item,
        reason: reason
      });
    });
  });

  return grouped;
};

/**
 * Format a reason with details for display
 * @param {Object} reason - The reason object with code, actual_value, limit, etc.
 * @returns {string} Formatted reason text
 */
export const formatReason = (reason) => {
  switch (reason.code) {
    case 'BENDING_SELECTED':
      return `Bending service selected (${reason.actual_value} bend${reason.actual_value !== 1 ? 's' : ''})`;

    case 'QUANTITY_EXCEEDS':
      return `Quantity exceeds limit (${reason.actual_value} units, max ${reason.limit})`;

    case 'NON_STOCK_MATERIAL':
      const matInfo = reason.material_info || {};
      return `Non-stock material selected (${matInfo.material_name || 'Unknown'} - ${matInfo.thickness || 'Unknown'})`;

    case 'WEIGHT_EXCEEDS':
      return `Weight exceeds limit (${reason.actual_value} lbs, max ${reason.limit} lbs)`;

    case 'WIDTH_EXCEEDS':
      return `Width exceeds limit (${reason.actual_value}" width, max ${reason.limit}")`;

    case 'HEIGHT_EXCEEDS':
      return `Height exceeds limit (${reason.actual_value}" height, max ${reason.limit}")`;

    default:
      return `Requires manual review (${reason.code})`;
  }
};
